/**
 * Documents tab component for QualificationDetail.
 * Displays compliance documents with expiry warnings and download links.
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  FileText,
  Download,
  CheckCircle2,
  Clock,
  AlertTriangle,
  XCircle,
  ExternalLink,
} from 'lucide-react';
import { SupplierQualification, ComplianceDocument, intelligenceAPI } from '../../lib/api';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useState } from 'react';
import FileDropZone from '../../components/common/FileDropZone';

interface DocumentsTabProps {
  qualification: SupplierQualification;
  isCustomer: boolean;
  onRefresh?: () => void;
}

/**
 * Format file size for display.
 */
function formatFileSize(bytes: number | undefined): string {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Get verification status display.
 */
function getVerificationStatus(doc: ComplianceDocument): {
  icon: React.ReactNode;
  text: string;
  className: string;
} {
  if (doc.is_verified) {
    return {
      icon: <CheckCircle2 className="h-4 w-4" />,
      text: 'Verified',
      className: 'text-green-600',
    };
  }
  return {
    icon: <Clock className="h-4 w-4" />,
    text: 'Pending',
    className: 'text-amber-600',
  };
}

/**
 * Get expiry status display.
 */
function getExpiryStatus(doc: ComplianceDocument): {
  icon: React.ReactNode;
  text: string;
  className: string;
} | null {
  if (!doc.expiry_date) return null;

  if (doc.is_expired) {
    return {
      icon: <XCircle className="h-4 w-4" />,
      text: 'Expired',
      className: 'text-red-600',
    };
  }

  if (doc.days_until_expiry !== undefined && doc.days_until_expiry <= 30) {
    return {
      icon: <AlertTriangle className="h-4 w-4" />,
      text: `Expires in ${doc.days_until_expiry} days`,
      className: 'text-amber-600',
    };
  }

  return {
    icon: <CheckCircle2 className="h-4 w-4" />,
    text: format(new Date(doc.expiry_date), 'MMM d, yyyy'),
    className: 'text-muted-foreground',
  };
}

export function DocumentsTab({ qualification, isCustomer, onRefresh }: DocumentsTabProps) {
  const documents = qualification.documents || [];
  const [uploading, setUploading] = useState(false);
  const [uploadExpiry, setUploadExpiry] = useState('');
  // Optional expiry date the user sets BEFORE choosing the file; sent with the
  // upload so certificates carry their expiry from the start.

  // Files attached as ANSWERS live on QualificationResponse.file_value, not on
  // ComplianceDocument — two separate stores. This tab only ever read the
  // second, so a supplier who attached evidence to a question saw
  // "No documents attached" and the customer never found the file at all.
  // Surface both, labelled by where they came from.
  const answerFiles = (qualification.responses || []).filter(r => r.file_value);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      await intelligenceAPI.uploadQualificationDocument(qualification.id, file, file.name, undefined, uploadExpiry || undefined);
      toast.success(`Uploaded ${file.name}`);
      onRefresh?.();
    } catch (err: unknown) {
      console.error('Error uploading document:', err);
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'Failed to upload document');
    } finally {
      setUploading(false);
      setUploadExpiry('');
    }
  };

  /** Fetch the bytes through the access-gated endpoint — the stored path is a
   *  server-side location, not a URL, so window.open on it always 404'd. */
  const fetchDocBlob = async (doc: ComplianceDocument, inline: boolean) => {
    const res = await intelligenceAPI.downloadQualificationDocument(qualification.id, doc.id, inline);
    return new Blob([res.data], { type: doc.mime_type || 'application/octet-stream' });
  };

  const handlePreview = async (doc: ComplianceDocument) => {
    try {
      const blob = await fetchDocBlob(doc, true);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err: unknown) {
      console.error('Error previewing document:', err);
      toast.error('Failed to open document');
    }
  };

  const handleSetExpiry = async (doc: ComplianceDocument, value: string) => {
    try {
      await intelligenceAPI.updateQualificationDocument(qualification.id, doc.id, { expiry_date: value || null });
      toast.success(value ? 'Expiry date set' : 'Expiry date cleared');
      onRefresh?.();
    } catch (err: unknown) {
      console.error('Error setting expiry:', err);
      toast.error('Failed to set expiry date');
    }
  };

  // Customer review 11 Sep (drag and drop everywhere): set the optional
  // expiry first, then drop the file on (or click) the zone.
  const uploadControl = (
    <div className="flex w-full max-w-xl flex-col gap-2 text-left">
      <div className="flex items-center gap-2">
        <label className="text-xs text-muted-foreground" htmlFor="qual-doc-expiry">Expiry (optional)</label>
        <input
          id="qual-doc-expiry"
          type="date"
          value={uploadExpiry}
          onChange={(e) => setUploadExpiry(e.target.value)}
          className="h-8 rounded-md border border-input bg-background px-2 text-sm"
        />
      </div>
      <FileDropZone
        size="compact"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.gif"
        busy={uploading}
        hint="PDF, Word, Excel, CSV or images"
        onFiles={(files) => void handleUpload(files[0])}
        data-testid="qualification-documents-drop-zone"
      />
    </div>
  );

  const answerFilesCard = answerFiles.length > 0 && (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="h-5 w-5" />
          Attached to answers ({answerFiles.length})
        </CardTitle>
        <CardDescription>
          Files the supplier attached while answering questions
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {answerFiles.map((r) => (
          <div key={r.id} className="flex items-center justify-between rounded-md border p-3">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{r.file_name || 'Attachment'}</p>
                {r.question_text && (
                  <p className="text-xs text-muted-foreground truncate">{r.question_text}</p>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const res = await intelligenceAPI.downloadResponseFile(qualification.id, r.question);
                  const url = URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = r.file_name || 'attachment';
                  a.click();
                  URL.revokeObjectURL(url);
                } catch {
                  toast.error('Failed to download file');
                }
              }}
            >
              <Download className="h-4 w-4 mr-1" />
              Download
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );

  const handleVerify = async (docId: number) => {
    try {
      await intelligenceAPI.verifyDocument(docId);
      toast.success('Document verified successfully');
      onRefresh?.();
    } catch (err: unknown) {
      console.error('Error verifying document:', err);
      toast.error('Failed to verify document');
    }
  };

  const handleDownload = async (doc: ComplianceDocument) => {
    try {
      const blob = await fetchDocBlob(doc, false);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.title || 'document';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      console.error('Error downloading document:', err);
      toast.error('Failed to download document');
    }
  };

  if (documents.length === 0) {
    return (
      <>
        <Card>
          <CardContent className="text-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No documents attached</h3>
            <p className="text-muted-foreground mb-4">
              {answerFiles.length > 0
                ? 'No separate compliance documents yet — files attached to answers are listed below.'
                : 'No compliance documents have been uploaded for this qualification.'}
            </p>
            <div className="flex justify-center">{uploadControl}</div>
          </CardContent>
        </Card>
        {answerFilesCard}
      </>
    );
  }

  return (
    <>
      <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Documents ({documents.length})
        </CardTitle>
        <CardDescription>
          Supporting documents and compliance evidence
        </CardDescription>
        <div className="pt-2">{uploadControl}</div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="font-semibold">Document</TableHead>
              <TableHead className="font-semibold">Uploaded</TableHead>
              <TableHead className="font-semibold">Expiry</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="text-right font-semibold">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.map((doc) => {
              const verificationStatus = getVerificationStatus(doc);
              const expiryStatus = getExpiryStatus(doc);

              return (
                <TableRow key={doc.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium truncate max-w-[200px]">
                          {doc.title || doc.file_name || 'Document'}
                        </div>
                        {doc.file_size && (
                          <div className="text-xs text-muted-foreground">
                            {formatFileSize(doc.file_size)}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {format(new Date(doc.uploaded_at), 'MMM d, yyyy')}
                    </div>
                    {doc.uploaded_by_name && (
                      <div className="text-xs text-muted-foreground">
                        by {doc.uploaded_by_name}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {expiryStatus && (
                      <div className={`flex items-center gap-1 text-sm ${expiryStatus.className}`}>
                        {expiryStatus.icon}
                        {expiryStatus.text}
                      </div>
                    )}
                    <input
                      type="date"
                      aria-label="Expiry date"
                      defaultValue={doc.expiry_date || ''}
                      onBlur={(e) => { if (e.target.value !== (doc.expiry_date || '')) handleSetExpiry(doc, e.target.value); }}
                      className="mt-1 h-7 rounded-md border border-input bg-background px-1.5 text-xs"
                    />
                  </TableCell>
                  <TableCell>
                    <div className={`flex items-center gap-1 text-sm ${verificationStatus.className}`}>
                      {verificationStatus.icon}
                      {verificationStatus.text}
                    </div>
                    {doc.is_verified && doc.verified_by_name && (
                      <div className="text-xs text-muted-foreground">
                        by {doc.verified_by_name}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownload(doc)}
                        title="Download"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePreview(doc)}
                        title="Preview in new tab"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                      {isCustomer && !doc.is_verified && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleVerify(doc.id)}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          Verify
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
      </Card>
      {answerFilesCard}
    </>
  );
}
